/**
 * Photos and messages on a Collab Pricing quote — Max, 2026-09-14:
 * "Thêm photo section and message section vào Collab Pricing Calculator", marked directly under the
 * SKU tab strip.
 *
 * One band, two panels, folded by default. The calculator is a dense screen and most quotes will
 * carry neither, so an unfolded pair of empty boxes would push the numbers down the page for nothing;
 * the header carries the counts, so a quote that HAS photos or a conversation says so without being
 * opened. A panel that already holds something opens itself the first time the quote is loaded.
 *
 * The photo strip is <ProjectPhotos>, the Project Tracker's component, under the new 'cpq' scope —
 * the same decision made when the sample and OPA screens needed photos: one store, one set of
 * endpoints, no second upload-and-delete to keep in step.
 *
 * Both need a SAVED quote: the owner key is the CP id, which does not exist until the first save.
 * That is stated on the band rather than hidden, because "nothing happens when I press it" is the
 * worst possible answer to an upload button.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image as ImageIcon, MessageSquare, ChevronDown, ChevronUp, Send, Trash2, Loader2 } from 'lucide-react';
import ProjectPhotos from './ProjectPhotos';
import { BACKEND_URL } from './backendConfig';
import { VN_TZ } from './timeZone';
import './CpAttachments.css';

const fmtWhen = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  // Vietnam time, like every other timestamp on the portal.
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: VN_TZ, day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(d);
};

const CpAttachments = ({
  projectId, readOnly = false,
  pendingPhotos = [], onPendingPhotos,
  pendingMessages = [], onPendingMessages,
  // How many photos the SAVED quote holds. The calculator needs it to refuse a save with none
  // (Max, 2026-09-14) and has no other way to know — the strip owns that list.
  onPhotoCount,
}) => {
  const { t } = useTranslation();
  const [photoCount, setPhotoCount] = useState(0);
  const [messages, setMessages] = useState([]);
  const [me, setMe] = useState(0);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  /* Open by default — Max, 2026-09-14: "mặc định luôn mở". The band was folded to keep a dense
     screen quiet; he would rather see both, and a panel can still be folded by hand. */
  const [open, setOpen] = useState({ photos: true, messages: true });

  const loadMessages = useCallback(async () => {
    if (!projectId) { setMessages([]); return; }
    try {
      const r = await fetch(`${BACKEND_URL}/api/pricing/projects/${encodeURIComponent(projectId)}/messages`,
        { credentials: 'include' });
      if (!r.ok) return;
      const d = await r.json();
      setMessages(d.messages || []);
      setMe(d.me || 0);
    } catch (e) { /* the band is an extra; a failed read must not break the calculator */ }
  }, [projectId]);

  const loadPhotoCount = useCallback(async () => {
    if (!projectId) { setPhotoCount(0); return; }
    try {
      const r = await fetch(`${BACKEND_URL}/api/development/photos/cpq/${encodeURIComponent(projectId)}`,
        { credentials: 'include' });
      if (!r.ok) return;
      const d = await r.json();
      setPhotoCount((d.photos || []).length);
    } catch (e) { /* as above */ }
  }, [projectId]);

  useEffect(() => { loadMessages(); loadPhotoCount(); }, [loadMessages, loadPhotoCount]);
  useEffect(() => { if (onPhotoCount) onPhotoCount(photoCount); }, [photoCount, onPhotoCount]);

  const post = async () => {
    const body = draft.trim();
    if (!body || busy) return;
    /* No CP number yet — Max, 2026-09-14: "messages có thể điền khi tạo luôn". Held by the
       calculator beside the photos and posted in order once the save gives the quote an id. */
    if (!projectId) {
      if (onPendingMessages) onPendingMessages([...pendingMessages, body]);
      setDraft('');
      return;
    }
    setBusy(true); setError('');
    try {
      const r = await fetch(`${BACKEND_URL}/api/pricing/projects/${encodeURIComponent(projectId)}/messages`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || t('cpAtt.saveFailed')); return; }
      setMessages(d.messages || []);
      setMe(d.me || 0);
      setDraft('');
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const remove = async (messageId) => {
    if (busy) return;
    /* A deleted message is now really gone (Max, 2026-09-17), so this asks first. The old
       withdrawal left the line in place and could be reasoned about afterwards; this cannot be
       undone from the screen, and the one click that does it sits beside every message. */
    if (!window.confirm(t('cpAtt.deleteConfirm'))) return;
    setBusy(true); setError('');
    try {
      const r = await fetch(`${BACKEND_URL}/api/pricing/projects/${encodeURIComponent(projectId)}/messages/${messageId}`,
        { method: 'DELETE', credentials: 'include' });
      const d = await r.json();
      if (!r.ok) { setError(d.error || t('cpAtt.deleteFailed')); return; }
      setMessages(d.messages || []);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const toggle = (key) => () => setOpen((o) => ({ ...o, [key]: !o[key] }));
  const live = messages.filter((m) => !m.deleted).length;

  return (
    <div className="cpa-band">
      <section className={`cpa-panel ${open.photos ? 'on' : ''}`}>
        <button type="button" className="cpa-head" onClick={toggle('photos')} aria-expanded={open.photos}>
          <ImageIcon size={14} />
          <strong>{t('cpAtt.photos')}</strong>
          <span className="cpa-n">{projectId ? photoCount : pendingPhotos.length}</span>
          {!projectId && !!pendingPhotos.length && <em className="cpa-need">{t('cpAtt.pendingTag')}</em>}
          {open.photos ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        {open.photos && (
          <div className="cpa-body">
            {projectId ? (
              /* The strip reports its list through onChange so the header's count stays honest. */
              <ProjectPhotos
                scope="cpq"
                ownerKey={projectId}
                readOnly={readOnly}
                hideTitle
                onChange={(photos) => setPhotoCount((photos || []).length)}
              />
            ) : (
              /* No CP number yet — Max, 2026-09-14: "ảnh được thêm từ lúc tạo luôn". The images are
                 compressed here and held by the calculator, which uploads them the moment the save
                 hands back an id. Nothing is written until then, so a quote that is never saved
                 leaves no files behind. */
              <ProjectPhotos
                scope="cpq"
                pending
                hideTitle
                pendingPhotos={pendingPhotos}
                onPendingChange={onPendingPhotos}
              />
            )}
          </div>
        )}
      </section>

      <section className={`cpa-panel ${open.messages ? 'on' : ''}`}>
        <button type="button" className="cpa-head" onClick={toggle('messages')} aria-expanded={open.messages}>
          <MessageSquare size={14} />
          <strong>{t('cpAtt.messages')}</strong>
          <span className="cpa-n">{projectId ? live : pendingMessages.length}</span>
          {!projectId && !!pendingMessages.length && <em className="cpa-need">{t('cpAtt.pendingTag')}</em>}
          {open.messages ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        {open.messages && (
          <div className="cpa-body">
            {!projectId ? (
              <>
                {!pendingMessages.length && <p className="cpa-empty">{t('cpAtt.noMessages')}</p>}
                <ul className="cpa-list">
                  {pendingMessages.map((body, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={i} className="held">
                      <div className="cpa-meta">
                        <b>{t('cpAtt.you')}</b>
                        <span>{t('cpAtt.pendingTag')}</span>
                        <button
                          type="button"
                          className="cpa-del"
                          onClick={() => onPendingMessages(pendingMessages.filter((_, j) => j !== i))}
                        >
                          <Trash2 size={11} /> {t('cpAtt.withdraw')}
                        </button>
                      </div>
                      <p>{body}</p>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <>
                {!!error && <p className="cpa-err">{error}</p>}
                {!messages.length && <p className="cpa-empty">{t('cpAtt.noMessages')}</p>}
                <ul className="cpa-list">
                  {messages.map((m) => (
                    <li key={m.messageId}>
                      <div className="cpa-meta">
                        <b>{m.author}</b>
                        <span>{fmtWhen(m.createdAt)}</span>
                        {/* Withdrawing is offered on your own line; the server also lets anybody
                            with the edit grant moderate, and refuses everything else. */}
                        {/* Deleting is offered on your own line; the server also lets anybody with
                            the edit grant moderate, and refuses everything else. */}
                        {Number(m.userId || 0) === Number(me || 0) && (
                          <button type="button" className="cpa-del" disabled={busy} onClick={() => remove(m.messageId)}>
                            <Trash2 size={11} /> {t('cpAtt.withdraw')}
                          </button>
                        )}
                      </div>
                      {/* No withdrawn state left to draw: a deleted message is not in the list. */}
                      <p>{m.body}</p>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {/* One composer for both states, and it stays open on a frozen quote on purpose — the
                numbers are locked, the conversation about them is not. */}
            <div className="cpa-write">
              <textarea
                rows={2}
                value={draft}
                placeholder={t('cpAtt.placeholder')}
                onChange={(e) => setDraft(e.target.value)}
              />
              <button type="button" className="sp-btn primary" disabled={busy || !draft.trim()} onClick={post}>
                {busy ? <Loader2 size={13} className="cpa-spin" /> : <Send size={13} />} {t('cpAtt.send')}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default CpAttachments;
